import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api, type ApiError } from '../lib/api';

export function KidLogin() {
  const nav = useNavigate();
  const { loginKid } = useAuth();
  const [familyCode, setFamilyCode] = useState('');
  const [screenName, setScreenName] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await loginKid({ familyCode: familyCode.toUpperCase(), screenName, pin });
      // If the kid arrived from an invite link, redeem it and go straight in.
      const pending = sessionStorage.getItem('kidzim_join');
      if (pending) {
        sessionStorage.removeItem('kidzim_join');
        const { room } = await api<{ room: { id: string } }>('/api/rooms/join', {
          method: 'POST',
          body: JSON.stringify({ token: pending }),
        });
        nav(`/chat/${room.id}`);
        return;
      }
      // Otherwise land on the kid's home: their buddies and chat rooms.
      nav('/buddies');
    } catch (err) {
      const e = err as ApiError;
      if (e.error === 'too_many_attempts') {
        const mins = e.retryAfter && e.retryAfter >= 60 ? `${Math.ceil(e.retryAfter / 60)} minutes` : 'a little while';
        setError(`Too many tries. Ask a grown-up and try again in ${mins}.`);
      } else if (e.error === 'invalid_credentials') {
        setError("Hmm, that didn't match. Ask a grown-up to check.");
      } else {
        setError('Could not sign you in.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 400, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
          <Logo height={40} />
        </div>
        <h3 style={{ textAlign: 'center', marginBottom: 'var(--space-5)' }}>Hi! Let's sign you in 👋</h3>
        <form className="kz-stack" onSubmit={submit}>
          <Input
            label="Family code"
            name="kidzim-family-code"
            placeholder="SUNNY-FOX"
            value={familyCode}
            onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            required
          />
          <Input
            label="Screen name"
            name="kidzim-screen-name"
            value={screenName}
            onChange={(e) => setScreenName(e.target.value)}
            autoCorrect="off"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            required
          />
          <Input
            label="Secret PIN"
            name="kidzim-pin"
            inputMode="numeric"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            required
          />
          {error && <p className="kz-error-text">{error}</p>}
          <Button type="submit" full disabled={busy}>
            {busy ? 'One sec…' : "Let's go!"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
